import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";
import {
    FiUser, FiMail, FiLock, FiPhone, FiMapPin,
    FiArrowRight, FiEye, FiEyeOff, FiBriefcase, FiLayers,
    FiX, FiAlertCircle, FiCheckCircle
} from "react-icons/fi";
import { FaTasks } from "react-icons/fa";

const CandidateRegister = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { jobId } = useParams();
    const verifiedEmail = location.state?.email || "";

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: verifiedEmail,
        password: "",
        phone: "",
        address: ""
    });

    useEffect(() => {
        if (verifiedEmail) {
            setFormData(prev => ({ ...prev, email: verifiedEmail }));
        }
    }, [verifiedEmail]);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [errors, setErrors] = useState({});

    // Validation functions
    const validateAlphabetsOnly = (value) => {
        return /^[A-Za-z\s]*$/.test(value);
    };

    const validateMobileNumber = (value) => {
        return /^[6-9]\d{9}$/.test(value);
    };


    const [showPassword, setShowPassword] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const [roleSearch, setRoleSearch] = useState("");
    const roleDropdownRef = useRef(null);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/roles/all`);
                let rolesData = [];
                if (Array.isArray(res.data)) rolesData = res.data;
                else if (Array.isArray(res.data.data)) rolesData = res.data.data;
                else if (Array.isArray(res.data.roles)) rolesData = res.data.roles;
                
                const uniqueRoles = rolesData.map((r, index) => {
                    if (typeof r === 'string') return r;
                    return r.name || r.title || r.role || "";
                }).filter(r => r !== "");
                
                setAvailableRoles([...new Set(uniqueRoles)]);
            } catch (err) {
                console.error("Failed to fetch roles:", err);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
                setShowRoleDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!showRoleDropdown) {
            setRoleSearch("");
        }
    }, [showRoleDropdown]);

    const handleRoleToggle = (role) => {
        setSelectedRoles(prev => 
            prev.includes(role) 
                ? prev.filter(r => r !== role) 
                : [...prev, role]
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Clear error for this field when user starts typing
        setErrors(prev => ({ ...prev, [name]: "" }));

        // Real-time validation
        if (name === 'firstName' || name === 'lastName') {
            if (value && !validateAlphabetsOnly(value)) {
                setErrors(prev => ({ ...prev, [name]: "Only alphabets allowed" }));
            } else if (value && value.length > 20) {
                setErrors(prev => ({ ...prev, [name]: "Maximum 20 characters allowed" }));
            }
        }

        if (name === 'phone') {
            if (value && !/^\d*$/.test(value)) {
                setErrors(prev => ({ ...prev, [name]: "Only numbers allowed" }));
            } else if (value && !/^[6-9]?\d{0,9}$/.test(value)) {
                setErrors(prev => ({ ...prev, [name]: "Must start with 6, 7, 8, or 9" }));
            }
        }

        if (name === 'address') {
            if (value && value.length > 100) {
                setErrors(prev => ({ ...prev, [name]: "Maximum 100 characters allowed" }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const newErrors = {};

        // First name validation
        if (!formData.firstName) {
            newErrors.firstName = "First name is required";
        } else if (!validateAlphabetsOnly(formData.firstName)) {
            newErrors.firstName = "Only alphabets allowed";
        } else if (formData.firstName.length > 20) {
            newErrors.firstName = "Maximum 20 characters allowed";
        }

        // Last name validation
        if (!formData.lastName) {
            newErrors.lastName = "Last name is required";
        } else if (!validateAlphabetsOnly(formData.lastName)) {
            newErrors.lastName = "Only alphabets allowed";
        } else if (formData.lastName.length > 20) {
            newErrors.lastName = "Maximum 20 characters allowed";
        }

        // Mobile number validation
        if (!formData.phone) {
            newErrors.phone = "Mobile number is required";
        } else if (!validateMobileNumber(formData.phone)) {
            newErrors.phone = "Must be 10 digits starting with 6, 7, 8, or 9";
        }

        // Location validation
        if (!formData.address) {
            newErrors.address = "Location is required";
        } else if (formData.address.length > 50) {
            newErrors.address = "Maximum 50 characters allowed";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setMessage({ text: "Please fix the errors before submitting", type: "error" });
            return;
        }

        if (selectedRoles.length === 0) {
            setMessage({ text: "Please select at least one role!", type: "error" });
            return;
        }

        setLoading(true);
        setMessage({ text: "", type: "" });

        const payload = {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            password: formData.password,
            role: selectedRoles.join(", ")
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/candidate/register`, payload);
            if (res.data.token) {
                const token = res.data.token;
                localStorage.setItem("candidateToken", token);

                // Fetch profile to get the definitive ID and Name
                try {
                    const profileRes = await axios.get(`${API_BASE_URL}/candidate/profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    localStorage.setItem("candidateId", profileRes.data._id);
                    localStorage.setItem("candidateName", profileRes.data.name);
                    localStorage.setItem("userRole", "candidate");
                } catch (profileErr) {
                    console.error("Profile fetch error after registration:", profileErr);
                    // Fallback to basic info if profile fetch fails
                    localStorage.setItem("candidateName", `${formData.firstName} ${formData.lastName}`);
                    localStorage.setItem("userRole", "candidate");
                }

                setMessage({ text: "Registration successful! Redirecting...", type: "success" });
                
                setTimeout(() => {
                    if (jobId) {
                        navigate(`/applying-job/${jobId}`);
                    } else {
                        navigate("/candidate-dashboard");
                    }
                }, 1500);
            }
        } catch (err) {
            console.error("Registration error:", err);
            setMessage({
                text: err.response?.data?.message || "Registration failed. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-start md:items-center justify-center font-sans py-0 md:p-6">
            <div className="w-full max-w-6xl bg-white md:rounded-[2.5rem] shadow-2xl shadow-indigo-100/50 overflow-hidden flex flex-col md:flex-row border-0 md:border border-slate-100 animate-in fade-in zoom-in duration-500 min-h-screen md:min-h-0">

                {/* Visual Side — compact banner on mobile, full panel on md+ */}
                <div className="md:w-5/12 bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-600 text-white relative overflow-hidden
                    flex flex-row items-center gap-4 px-5 py-5
                    md:flex-col md:items-start md:justify-between md:p-12">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_-20%_-20%,rgba(255,255,255,0.2)_0%,transparent_60%)]"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl hidden md:block"></div>

                    {/* Icon + Title row for mobile */}
                    <div className="relative z-10 flex items-center gap-3 md:block">
                        <div className="w-10 h-10 md:w-14 md:h-14 bg-white/20 rounded-xl md:rounded-2xl flex items-center justify-center md:mb-10 backdrop-blur-md border border-white/30 shadow-xl shrink-0">
                            <FaTasks className="text-lg md:text-2xl" />
                        </div>
                        <div className="md:hidden">
                            <h2 className="text-base font-black leading-tight">Build your professional profile.</h2>
                            <p className="text-[11px] text-indigo-100/80 font-medium">Join Timely Health's talent community.</p>
                        </div>
                    </div>

                    {/* Full text — desktop only */}
                    <div className="relative z-10 hidden md:block">
                        <h2 className="text-4xl font-black leading-tight mb-4">
                            Build your <br />
                            <span className="text-indigo-200">professional profile.</span>
                        </h2>
                        <p className="text-lg text-indigo-100/80 font-medium leading-relaxed">
                            Join our talent community and land your dream job at Timely Health.
                        </p>
                    </div>

                    <div className="relative z-10 space-y-6 hidden md:block">
                        <div className="flex items-center gap-4 group">
                            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-all">
                                <FiCheckCircle className="text-indigo-200" />
                            </div>
                            <span className="text-xs font-bold text-indigo-100 uppercase tracking-wider">Quick 1-minute setup</span>
                        </div>
                        <div className="flex items-center gap-4 group">
                            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-all">
                                <FiCheckCircle className="text-indigo-200" />
                            </div>
                            <span className="text-xs font-bold text-indigo-100 uppercase tracking-wider">Apply with one click</span>
                        </div>
                    </div>
                </div>

                {/* Form Side */}
                <div className="md:w-7/12 px-5 py-7 md:p-16 flex flex-col justify-center overflow-y-auto no-scrollbar bg-white">
                    <div className="mb-7 md:mb-12">
                        <h1 className="text-xl md:text-2xl font-black text-slate-800 mb-1">Create Account</h1>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Complete your registration
                        </p>
                    </div>

                    {message.text && (
                        <div className={`mb-5 p-3.5 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 border ${message.type === "success"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                            : "bg-rose-50 text-rose-600 border-rose-100"
                            }`}>
                            {message.type === "success" ? <FiCheckCircle className="text-lg shrink-0" /> : <FiAlertCircle className="text-lg shrink-0" />}
                            <span className="text-sm font-bold">{message.text}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                        <div className="grid grid-cols-2 gap-3 md:gap-6">
                            {/* First Name */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiUser className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        required
                                        className={`w-full pl-9 md:pl-11 pr-3 py-3 bg-white border rounded-xl focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm ${errors.firstName ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-600'}`}
                                        placeholder="Alex"
                                    />
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Enter your first name (letters only)</p>
                                {errors.firstName && <p className="text-[9px] font-bold text-red-500 ml-1">{errors.firstName}</p>}
                            </div>

                            {/* Last Name */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiUser className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        required
                                        className={`w-full pl-9 md:pl-11 pr-3 py-3 bg-white border rounded-xl focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm ${errors.lastName ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-600'}`}
                                        placeholder="Johnson"
                                    />
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Enter your last name (letters only)</p>
                                {errors.lastName && <p className="text-[9px] font-bold text-red-500 ml-1">{errors.lastName}</p>}
                            </div>

                            {/* Email - Verified */}
                            <div className="space-y-1.5 col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiMail className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-9 md:pl-11 pr-3 py-3 bg-indigo-50 border border-indigo-200 rounded-xl outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm cursor-not-allowed"
                                        placeholder="alex@example.com"
                                        readOnly
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">✓ Verified</span>
                                    </div>
                                </div>
                                <p className="text-[9px] text-emerald-600 ml-1 font-medium">✅ This email has been verified. You'll use this email to log in.</p>
                            </div>

                            {/* Password */}
                            <div className="space-y-1.5 col-span-2 md:col-span-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Create Password</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiLock className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-9 md:pl-11 pr-10 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm"
                                        placeholder="Create a strong password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-indigo-600 transition-colors"
                                    >
                                        {showPassword ? <FiEyeOff className="text-base" /> : <FiEye className="text-base" />}
                                    </button>
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Create a new password (minimum 6 characters with letters & numbers). You'll use this password along with the verified email above to log in.</p>
                            </div>

                            {/* Mobile */}
                            <div className="space-y-1.5 col-span-2 md:col-span-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Mobile Number</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiPhone className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        maxLength={10}
                                        className={`w-full pl-9 md:pl-11 pr-3 py-3 bg-white border rounded-xl focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm ${errors.phone ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-600'}`}
                                        placeholder="9876543210"
                                    />
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Enter your 10-digit mobile number for contact purposes</p>
                                {errors.phone && <p className="text-[9px] font-bold text-red-500 ml-1">{errors.phone}</p>}
                            </div>

                            {/* Address */}
                            <div className="space-y-1.5 col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Location / Address</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                                        <FiMapPin className="text-base md:text-lg" />
                                    </div>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        required
                                        maxLength={100}
                                        className={`w-full pl-9 md:pl-11 pr-3 py-3 bg-white border rounded-xl focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all text-sm font-medium text-slate-700 placeholder:text-slate-300 shadow-sm ${errors.address ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-indigo-600'}`}
                                        placeholder="Mumbai, Maharashtra"
                                    />
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Enter your city and state where you're currently located</p>
                                {errors.address && <p className="text-[9px] font-bold text-red-500 ml-1">{errors.address}</p>}
                            </div>

                            {/* Desired Roles */}
                            <div className="space-y-1.5 col-span-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Desired Roles (Select Multiple)</label>
                                <div className="relative" ref={roleDropdownRef}>
                                    <div 
                                        onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                                        className="w-full min-h-[46px] pl-3 md:pl-4 pr-8 py-2 bg-white border border-slate-200 rounded-xl outline-none transition-all text-sm font-medium text-slate-700 shadow-sm flex flex-wrap gap-1.5 items-center cursor-pointer"
                                    >
                                        {selectedRoles.length === 0 ? (
                                            <span className="text-slate-300 text-sm">Select roles...</span>
                                        ) : (
                                            selectedRoles.map((role) => (
                                                <span 
                                                    key={role} 
                                                    className="bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRoleToggle(role);
                                                    }}
                                                >
                                                    {role}
                                                    <FiX className="hover:text-indigo-800 cursor-pointer text-xs" />
                                                </span>
                                            ))
                                        )}
                                    </div>
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </div>

                                    {showRoleDropdown && (
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto no-scrollbar p-2 space-y-1">
                                            <div className="px-1 py-1 sticky top-0 bg-white border-b border-slate-100 mb-2 z-10">
                                                <input 
                                                    type="text"
                                                    value={roleSearch}
                                                    onChange={(e) => setRoleSearch(e.target.value)}
                                                    placeholder="Search roles..."
                                                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-indigo-600 font-medium"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            {availableRoles.filter(role => role.toLowerCase().includes(roleSearch.toLowerCase())).length === 0 ? (
                                                <div className="p-3 text-xs text-slate-400 text-center font-bold uppercase">No matching roles</div>
                                            ) : (
                                                availableRoles.filter(role => role.toLowerCase().includes(roleSearch.toLowerCase())).map((role) => {
                                                    const isSelected = selectedRoles.includes(role);
                                                    return (
                                                        <div
                                                            key={role}
                                                            onClick={() => handleRoleToggle(role)}
                                                            className={`px-3 py-2 text-xs font-bold rounded-lg cursor-pointer flex items-center justify-between transition-colors ${
                                                                isSelected 
                                                                    ? "bg-indigo-50 text-indigo-600" 
                                                                    : "text-slate-600 hover:bg-slate-50"
                                                            }`}
                                                        >
                                                            <span>{role}</span>
                                                            {isSelected && <FiCheckCircle className="text-indigo-600" />}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-[9px] text-slate-400 ml-1 font-medium">Select one or more roles you're interested in for job recommendations</p>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 md:py-5 bg-indigo-600 text-white font-black rounded-xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:pointer-events-none group mt-2 uppercase tracking-widest text-sm"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>
                                    <span>Create Account</span>
                                    <FiArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-6 md:mt-10 text-center text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                        Already have an account?{" "}
                        <Link to={jobId ? `/candidate-login/${jobId}` : "/candidate-login"} className="text-indigo-600 font-black hover:underline underline-offset-8 transition-all">
                            Log In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CandidateRegister;